import { ActionButton } from "@via/components/action-button/action-button.tsx";
import Drawer from "@via/components/drawer.tsx";
import { PageHeading } from "@via/components/page-heading.tsx";
import useUrlParams from "@via/hooks/useURLParams";
import CreateProjectForm from "@via/pages/projects/create-project-form.tsx";
import DeleteProjectModal from "@via/pages/projects/delete-project-modal.tsx";
import ProjectsTable from "@via/pages/projects/projects-table.tsx";
import { BUTTONS, PAGE_NAMES } from "@via/utils/constants.tsx";
import { TableContextMenuInterface } from "@via/utils/interfaces";
import { ProjectViewType, type ListProjectsResponseBodyItem } from "common";
import { useEffect, useState, type JSX } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const defaultParams = {
  sort: [{ id: "updatedAt", desc: true }],
  filters: [{ id: "type", value: ProjectViewType.MyProjects }],
  page: 0,
  pageSize: 10,
};

export default function Projects(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();
  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );
  const [columnFilters, setColumnFilters] = useState<
    Array<{ id: string; value: unknown }>
  >(initialParams.filters);
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  const [projectToBeDeleted, setProjectToBeDeleted] =
    useState<ListProjectsResponseBodyItem | null>(null);

  useEffect(() => {
    const newProject = searchParams.get("new");
    if (newProject) {
      setDrawerOpen(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isDrawerOpen) {
      searchParams.delete("new");
      navigate({
        pathname: "/projects",
        search: searchParams.toString(),
      });
    }
  }, [isDrawerOpen]);

  const handleAction = (
    option: TableContextMenuInterface,
    project: ListProjectsResponseBodyItem
  ): void => {
    switch (option.name) {
      case "Delete Project":
        setProjectToBeDeleted(project);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    setUrlParams({
      sort,
      filters: columnFilters,
      page,
      pageSize,
    });
  }, [sort, columnFilters, page, pageSize]);

  return (
    <main className="via-content-wrapper">
      <div className="via-page-heading-wrapper">
        <PageHeading title={PAGE_NAMES.PROJECTS} />
        <ActionButton
          button={BUTTONS.NEW_PROJECT}
          onClick={() => {
            setDrawerOpen(true);
          }}
        />
        <Drawer id="create-project" open={isDrawerOpen} setOpen={setDrawerOpen}>
          <CreateProjectForm setOpen={setDrawerOpen} />
        </Drawer>
        {!!projectToBeDeleted && (
          <DeleteProjectModal
            project={projectToBeDeleted}
            setProject={() => {
              setProjectToBeDeleted(null);
            }}
          />
        )}
      </div>
      <ProjectsTable
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        sort={sort}
        setSort={setSort}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        handleAction={handleAction}
      />
    </main>
  );
}
